// Pure helpers behind "refresh the on-screen record after a chat mutation".
//
// Chat mutations run on crm-chat-api -> Hasura, so the CRM Apollo cache never
// sees the write. Rather than refetching everything mounted (a CMA-from-scratch
// turn would reload an unrelated page several times), the panel refetches only
// the active queries whose variables reference the record in the current URL,
// and only when a successful mutation mentions that record.
//
// Kept free of React/Apollo imports so it runs under `pnpm test:node`.

type ToolPartLike = {
  type: string;
  state?: string;
  toolName?: string;
  input?: unknown;
  output?: unknown;
};

const RECORD_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Mirrors ADD_PROPERTY_IMAGES_TOOL_NAME in AddPropertyImagesToolCard.tsx. Kept
// as a local literal so this module stays importable from node tests.
const ADD_PROPERTY_IMAGES_TOOL = 'add_property_images';

const THEME_WRITE_TOOLS = new Set([
  'preview_team_theme',
  'save_team_theme',
  'delete_team_theme',
  'create_team_template',
  'update_document_template',
  'undo_document_template_change',
]);

const isToolPart = (part: ToolPartLike): boolean =>
  part.type.startsWith('tool-') || part.type === 'dynamic-tool';

const toolNameOf = (part: ToolPartLike): string =>
  part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.slice('tool-'.length);

const stringifyLoose = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
};

/** First UUID-looking path segment, e.g. `/leads/<id>` or `/cma-reports/<id>/page-cover`. */
export const extractRecordId = (pathname: string): string | null => {
  const withoutQuery = pathname.split(/[?#]/)[0];
  const segment = withoutQuery.split('/').find(s => RECORD_ID_RE.test(s));
  return segment ? segment.toLowerCase() : null;
};

export type ThemeDocumentKind = 'cma_report' | 'lot_brochure';

export const themeDocumentKindOf = (
  pathname: string,
): ThemeDocumentKind | null => {
  const segments = (pathname.split(/[?#]/)[0] ?? '').split('/').filter(Boolean);
  for (let index = 0; index < segments.length - 1; index += 1) {
    if (!RECORD_ID_RE.test(segments[index + 1])) {
      continue;
    }
    const section = segments[index];
    if (section === 'cma-reports') {
      return 'cma_report';
    }
    if (
      section === 'brochure-reports' ||
      (section === 'listings' && segments[index + 2] === 'brochure')
    ) {
      return 'lot_brochure';
    }
  }
  return null;
};

export const themeSwitchLinkOf = (
  input: unknown,
  output: unknown,
): { kind: ThemeDocumentKind; path: string } | null => {
  if (
    typeof input !== 'object' ||
    input == null ||
    typeof output !== 'object' ||
    output == null
  ) {
    return null;
  }
  const { applyTo } = input as { applyTo?: unknown };
  const { ok, documentsSwitched, otherDocumentType } = output as {
    ok?: unknown;
    documentsSwitched?: unknown;
    otherDocumentType?: unknown;
  };
  const id = Array.isArray(applyTo) ? applyTo[0] : null;
  if (
    ok !== true ||
    typeof documentsSwitched !== 'number' ||
    documentsSwitched < 1 ||
    typeof id !== 'string' ||
    !RECORD_ID_RE.test(id)
  ) {
    return null;
  }
  if (otherDocumentType === 'cma_report') {
    return { kind: 'cma_report', path: `/cma-reports/${id}/page-cover` };
  }
  if (otherDocumentType === 'lot_brochure') {
    return { kind: 'lot_brochure', path: `/listings/${id}/brochure` };
  }
  return null;
};

/**
 * A mutation tool part that actually wrote something.
 *
 * crm-chat-api never throws from `graphql_mutation`: every failure comes back as
 * a plain text result in `output-available` ("GraphQL errors: …", "Mutation timed
 * out …", "Execution failed: …", …). A success is always
 * `JSON.stringify(json.data, null, 2)`, possibly truncated, so it starts with `{`.
 * `add_property_images` returns an object with `error` / `insertedCount`.
 */
export const isSuccessfulMutationPart = (part: ToolPartLike): boolean => {
  if (!isToolPart(part) || part.state !== 'output-available') {
    return false;
  }
  const toolName = toolNameOf(part);
  if (toolName === ADD_PROPERTY_IMAGES_TOOL) {
    const output = part.output;
    if (typeof output !== 'object' || output == null) {
      return false;
    }
    const { error, insertedCount } = output as {
      error?: unknown;
      insertedCount?: unknown;
    };
    return (
      error == null && typeof insertedCount === 'number' && insertedCount > 0
    );
  }
  if (THEME_WRITE_TOOLS.has(toolName)) {
    return (
      typeof part.output === 'object' &&
      part.output != null &&
      (part.output as { ok?: unknown }).ok === true
    );
  }
  if (toolName.endsWith('_mutation')) {
    return (
      typeof part.output === 'string' && part.output.trimStart().startsWith('{')
    );
  }
  return false;
};

export const isThemeWriteSuccess = (part: ToolPartLike): boolean =>
  THEME_WRITE_TOOLS.has(toolNameOf(part)) && isSuccessfulMutationPart(part);

/** Does the mutation's input (document + variables) or its result mention this record? */
export const mutationMentionsRecord = (
  part: ToolPartLike,
  recordId: string,
): boolean => {
  const needle = recordId.toLowerCase();
  return (
    stringifyLoose(part.input).toLowerCase().includes(needle) ||
    stringifyLoose(part.output).toLowerCase().includes(needle)
  );
};

/**
 * Ids whose active queries should refresh after this successful mutation, given
 * the record in the current URL (if any).
 *
 * - Any mutation that mentions the URL record refreshes that record.
 * - `add_property_images` is keyed by property (and optionally CMA report), not
 *   by the lot/lead in the URL, so its own `propertyId` / `cmaReportId` are added
 *   as well. Nothing fires unless an active query actually references them.
 */
export const collectRefreshIds = (
  part: ToolPartLike,
  currentRecordId: string | null,
): string[] => {
  const ids = new Set<string>();
  if (
    currentRecordId != null &&
    mutationMentionsRecord(part, currentRecordId)
  ) {
    ids.add(currentRecordId.toLowerCase());
  }
  if (toolNameOf(part) === ADD_PROPERTY_IMAGES_TOOL) {
    for (const source of [part.output, part.input]) {
      if (typeof source !== 'object' || source == null) {
        continue;
      }
      const { propertyId, cmaReportId } = source as {
        propertyId?: unknown;
        cmaReportId?: unknown;
      };
      for (const id of [propertyId, cmaReportId]) {
        if (typeof id === 'string' && RECORD_ID_RE.test(id)) {
          ids.add(id.toLowerCase());
        }
      }
    }
  }
  return [...ids];
};

/**
 * Marks every eligible tool part not claimed yet as claimed, and gathers the ids
 * to refresh for it. A part whose call id is in `alreadyRefreshed` is claimed
 * but adds no ids (its queries were refetched when it arrived).
 */
export const claimRefreshIds = <
  P extends ToolPartLike & { toolCallId?: string | null },
>(
  parts: ReadonlyArray<P>,
  currentRecordId: string | null,
  claimed: Set<string>,
  isEligible: (part: P) => boolean,
  alreadyRefreshed?: ReadonlySet<string>,
): { claimedAny: boolean; ids: Set<string> } => {
  const ids = new Set<string>();
  let claimedAny = false;
  for (const part of parts) {
    const { toolCallId } = part;
    if (toolCallId == null || claimed.has(toolCallId) || !isEligible(part)) {
      continue;
    }
    claimed.add(toolCallId);
    claimedAny = true;
    if (alreadyRefreshed?.has(toolCallId)) {
      continue;
    }
    for (const id of collectRefreshIds(part, currentRecordId)) {
      ids.add(id);
    }
  }
  return { claimedAny, ids };
};

/**
 * Ids to refresh after a `compose_email` draft was sent: the records the email
 * is linked to (its `parents`) and the buyer leads of its recipients, whatever
 * record is in the URL.
 */
export const composeEmailRefreshIds = (input: unknown): string[] => {
  const { parents, to } =
    typeof input === 'object' && input != null
      ? (input as { parents?: unknown; to?: unknown })
      : { parents: null, to: null };
  const fieldOf = (item: unknown, field: 'id' | 'buyerLeadId'): unknown =>
    typeof item === 'object' && item != null
      ? (item as Record<string, unknown>)[field]
      : null;
  const candidates = [
    ...(Array.isArray(parents) ? parents.map(p => fieldOf(p, 'id')) : []),
    ...(Array.isArray(to) ? to.map(r => fieldOf(r, 'buyerLeadId')) : []),
  ];
  const ids = new Set<string>();
  for (const id of candidates) {
    if (typeof id === 'string' && RECORD_ID_RE.test(id)) {
      ids.add(id.toLowerCase());
    }
  }
  return [...ids];
};

/** `onQueryUpdated` filter: only refetch queries whose variables reference one of the ids. */
export const queryVariablesMentionAnyRecord = (
  variables: unknown,
  recordIds: ReadonlyArray<string>,
): boolean => {
  if (variables == null || recordIds.length === 0) {
    return false;
  }
  const haystack = stringifyLoose(variables).toLowerCase();
  return recordIds.some(id => haystack.includes(id.toLowerCase()));
};

// Query params owned by the list page (ListToolbar, pagination, view toggle)
// rather than by the record page itself. Sourced from Leads/Listings/… list
// views and `_shared/utils/paginations.ts`. Record pages keep their own state
// under other keys (`buyers_view`, `create_offer`, `buyerId`, `opened`, …).
const LIST_OWNED_PARAMS = new Set([
  'where',
  'order_by',
  'view',
  'search',
  'offset',
  'limit',
  'show_real_count',
  // SmartListTitle writes this next to `where` (ListToolbar clears both).
  'smart_list_id',
  // RaDataGrid column/density preferences.
  'visible_columns',
  'density',
]);

const splitPathAndSearch = (
  url: string,
): { pathname: string; search: string } => {
  const withoutHash = url.split('#')[0];
  const [rawPath, ...rest] = withoutHash.split('?');
  const trimmed = rawPath.replace(/\/+$/, '');
  return {
    pathname: trimmed === '' ? '/' : trimmed,
    search: rest.join('?'),
  };
};

/**
 * Would navigating from `current` to `dest` change nothing the user can see?
 *
 * True when the pathnames match (trailing slash and hash ignored) and the
 * destination carries the same query string, or no query string *on a record
 * path whose current params are all list-owned* (agent sent a bare
 * `/leads/<id>` while the list behind the drawer holds `?view=…` — that query
 * belongs to the list, not the page). If the record page keeps its own state
 * in the query (`/listings/<id>/buyers?buyers_view=kanban`), a bare
 * destination resets that state and must navigate. On a list path a bare
 * destination clears the filter (`/leads?where=…` → `/leads`), a real state
 * change that must navigate; so must any *different* query string (`?where=…`
 * filter deep-links, `?tab=`…). Both inputs must be basename-relative
 * (`useLocation()` is; `resolveNavigatePath` strips the locale).
 */
export const isNavigateNoop = (current: string, dest: string): boolean => {
  const from = splitPathAndSearch(current);
  const to = splitPathAndSearch(dest);
  if (from.pathname !== to.pathname) {
    return false;
  }
  if (to.search === from.search) {
    return true;
  }
  return (
    to.search === '' &&
    extractRecordId(to.pathname) != null &&
    [...new URLSearchParams(from.search).keys()].every(key =>
      LIST_OWNED_PARAMS.has(key),
    )
  );
};
